/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Directionality } from '@angular/cdk/bidi';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
// eslint-disable-next-line consistent-default-export-name/default-import-match-filename
import localeAr from '@angular/common/locales/ar';
// eslint-disable-next-line consistent-default-export-name/default-import-match-filename
import localeCy from '@angular/common/locales/cy';
// eslint-disable-next-line consistent-default-export-name/default-import-match-filename
import localeDe from '@angular/common/locales/de';
// eslint-disable-next-line consistent-default-export-name/default-import-match-filename
import localeEnGb from '@angular/common/locales/en-GB';
// eslint-disable-next-line consistent-default-export-name/default-import-match-filename
import localeFr from '@angular/common/locales/fr';
import { APP_INITIALIZER, ErrorHandler, NgModule, NgZone } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as Sentry from '@sentry/angular';

import { environment } from '../environments/environment';

import { MainComponent } from './index/components/main/main.component';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from './page-modules/account/services/account-data.service';
import { ApiAccountDataService } from './page-modules/account/services/api-account-data.service';
import { ApiGoogleCalendarDataService } from './page-modules/account/services/api-google-calendar-data.service';
import { ApiOutlookDataService } from './page-modules/account/services/api-outlook-data.service';
import { GOOGLE_CALENDAR_DATA_SERVICE, GoogleCalendarDataService } from './page-modules/account/services/google-calendar-data.service';
import { MockAccountDataService } from './page-modules/account/services/mock-account-data.service';
import { OUTLOOK_DATA_SERVICE, OutlookDataService } from './page-modules/account/services/outlook-data.service';
import { REGISTRATIONS_DATA_SERVICE } from './page-modules/admin/services/registrations/registrations-data.service';
import { registrationsServiceFactory } from './page-modules/admin/services/registrations/registrations.factory';
import { ApiMyHubDataService } from './page-modules/my-hub/services/api-data.service';
import { MY_HUB_DATA_SERVICE, MyHubDataService } from './page-modules/my-hub/services/data.service';
import { MockMyHubDataService } from './page-modules/my-hub/services/mock-data.service';

import { AppRoutingModule } from './routing/routing.module';
import { DialogService } from './shared/helpers/dialog/dialog.service';
import { ApiSharedCardDataService } from './shared/services/card/api-card-data.service';
import { SHARED_CARD_DATA_SERVICE, SharedCardDataService } from './shared/services/card/card-data.service';
import { MockSharedCardDataService } from './shared/services/card/mock-card-data.service';
import { ApiCollectorDataService } from './shared/services/collector/api-collector-data.service';
import { COLLECTOR_DATA_SERVICE, CollectorDataService } from './shared/services/collector/collector-data.service';
import { MockCollectorDataService } from './shared/services/collector/mock-collector-data.service';
import { CORE_COMPETENCE_DATA_SERVICE } from './shared/services/competence/competence-data.service';
import { AwsFileUploadService } from './shared/services/file-upload/aws-file-upload.service';
import {
  FileUploadService,
  ORGANIZATIONS_FILE_UPLOAD_DATA_SERVICE,
  RESOURCES_FILE_UPLOAD_DATA_SERVICE,
} from './shared/services/file-upload/file-upload.service';
import { MockFileUploadService } from './shared/services/file-upload/mock-file-upload.service';
import { HttpConfigInterceptor } from './shared/services/http-config-interceptor';
import { ApiLearnerPlaylistDataService } from './shared/services/learner-playlist/api-data.service';

import { ApiOrganizationRetrievalDataService } from '@app/app/shared/services/organization-retrieval/api-organization-retrieval-data.service';
import { MockOrganizationRetrievalDataService } from '@app/app/shared/services/organization-retrieval/mock-organization-retrieval-data.service';
import {
  ORGANIZATION_RETRIEVAL_DATA_SERVICE,
  OrganizationRetrievalDataService,
} from '@app/app/shared/services/organization-retrieval/organization-retrieval-data.service';
import { TranslocoService } from '@ngneat/transloco';
import {
  ADMIN_ENROLMENTS_DATA_SERVICE,
  AdminEnrolmentsDataService,
} from './shared/services/admin-enrolments/admin-enrolments-data.service';
import { ApiAdminEnrolmentsDataService } from './shared/services/admin-enrolments/api-admin-enrolments-data.service';
import { MockAdminEnrolmentsDataService } from './shared/services/admin-enrolments/mock-admin-enrolments-data.service';
import { BOOKING_DATA_SERVICE, BookingDataService } from './shared/services/booking/booking-data.service';
import { BookingFormsBookingDataService } from './shared/services/booking/booking-forms-booking-data.service';
import { MockBookingDataService } from './shared/services/booking/mock-booking-data.service';
import { ApiV3BookingDataService } from './shared/services/bookings/v3-booking-data.service';
import { ApiDataFormDataService } from './shared/services/data-form/api-data-form-data.service';
import { DATA_FORM_DATA_SERVICE, DataFormDataService } from './shared/services/data-form/data-form-data.service';
import { MockDataFormDataService } from './shared/services/data-form/mock-data-form-data.service';
import { ApiGroupListDataService } from './shared/services/group-list/api-group-list-data.service';
import { GROUP_LIST_DATA_SERVICE, GroupListDataService } from './shared/services/group-list/group-list-data.service';
import { MockGroupListDataService } from './shared/services/group-list/mock-group-list-data.service';
import { ApiInvitationsDataService } from './shared/services/invitations/api-invitations-data.service';
import { INVITATIONS_DATA_SERVICE, InvitationsDataService } from './shared/services/invitations/invitations-data.service';
import { MockInvitationsDataService } from './shared/services/invitations/mock-invitations-data.service';
import { joinServiceFactory } from './shared/services/join/join-data.factory';
import { JOIN_DATA_SERVICE } from './shared/services/join/join-data.service';
import { ApiLanguagesDataService } from './shared/services/languages/api-languages-data.service';
import { DEFAULT_LANGUAGE_CODE, FALLBACK_LANGUAGE_CODE, LANGUAGE_KEY, LanguageService } from './shared/services/languages/language.service';
import { LANGUAGES_DATA_SERVICE, LanguagesDataService } from './shared/services/languages/languages-data.service';
import { MockLanguagesDataService } from './shared/services/languages/mock-languages-data.service';
import { LEARNER_PLAYLIST_DATA_SERVICE, LearnerPlaylistDataService } from './shared/services/learner-playlist/data.service';
import { MockLearnerPlaylistDataService } from './shared/services/learner-playlist/mock-data.service';
import { ApiLikeInfoDataService } from './shared/services/like-info/api-like-info-data.service';
import { LIKE_INFO_DATA_SERVICE, LikeInfoDataService } from './shared/services/like-info/like-info-data.service';
import { LikeInfoSubscriptionService } from './shared/services/like-info/like-info-subscription.service';
import { MockLikeInfoDataService } from './shared/services/like-info/mock-like-info-data.service';
import { NavigationHistoryService } from './shared/services/navigation-history.service';
import { ApiOptionListDataService } from './shared/services/option-list/api-option-list-data.service';
import { MockOptionListDataService } from './shared/services/option-list/mock-option-list-data.service';
import { OPTION_LIST_DATA_SERVICE, OptionListDataService } from './shared/services/option-list/option-list-data.service';
import { PageTitleService } from './shared/services/page-title/page-title.service';
import { ApiPIDataService } from './shared/services/pi/api-pi-data.service';
import { MockPIDataService } from './shared/services/pi/mock-pi-data.service';
import { PIDataService, PI_DATA_SERVICE } from './shared/services/pi/pi-data.service';
import { PreviousRouteService } from './shared/services/previous-router.service';
import { RestClientService } from './shared/services/rest-client.service';
import { ApiSharedReviewDataService } from './shared/services/review/api-review-data.service';
import { MockSharedReviewDataService } from './shared/services/review/mock-review-data.service';
import { SHARED_REVIEW_DATA_SERVICE, SharedReviewDataService } from './shared/services/review/review-data.service';
import { ApiSidebarTreeDataService } from './shared/services/sidebar-tree/api-sidebar-tree-data.service';
import { MockSidebarTreeDataService } from './shared/services/sidebar-tree/sidebar-tree-card-data.service';
import { SIDEBAR_TREE_DATA_SERVICE, SidebarTreeDataService } from './shared/services/sidebar-tree/sidebar-tree-data.service';
import { ApiSubheaderDataService } from './shared/services/subheader/api-subheader-data.service';
import { MockSubheaderDataService } from './shared/services/subheader/mock-subheader-data.service';
import { SUBHEADER_DATA_SERVICE, SubheaderDataService } from './shared/services/subheader/subheader-data.service';
import { ApiTagDataService } from './shared/services/tags/api-tag-data.service';
import { MockTagDataService } from './shared/services/tags/mock-tag-data.service';
import { CORE_TAG_DATA_SERVICE, TagDataService } from './shared/services/tags/tag-data.service';
import { MockToastService } from './shared/services/toast-notifications/mock-toast-service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from './shared/services/toast-notifications/toast-service';
import { ToastServiceImpl } from './shared/services/toast-notifications/toast-service-impl';
import { ApiWebsocketService } from './shared/services/websocket/api-websocket.service';
import { MockWebsocketService } from './shared/services/websocket/mock-websocket.service';
import { WEBSOCKET_SERVICE, WebsocketService } from './shared/services/websocket/websocket.service';
import * as SharedFactoryService from './shared/shared-service-factory';
import { TranslocoRootModule } from './transloco/transloco-root.module';
import { AccessTokenService } from './user-auth/services/access-token.service';

import { Router } from '@angular/router';
import { EMBEDS_CONTENT_GENERATOR_SERVICE, EmbedsContentGeneratorService } from '@app/app/editor/services/embeds-content-generator.service';
import { IframelyEmbedsContentGeneratorService } from '@app/app/editor/services/iframely-embeds-content-generator.service';
import { ShareService } from '@app/app/shared/services/share/share.service';
import { NgxsModule } from '@ngxs/store';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { PAGES_DATA_SERVICE } from './page-modules/pages/services/data.service';
import { PROJECT_DATA_SERVICE } from './page-modules/project/services/data.service';
import { ApiSharedListsDataService } from './shared/services/lists/api-lists-data.service';
import { SHARED_LISTS_DATA_SERVICE, SharedListsDataService } from './shared/services/lists/lists-data.service';
import { MockSharedListsDataService } from './shared/services/lists/mock-lists-data.service';
import { MaintenanceService } from './shared/services/maintenance/maintenance.service';
import { RequestQueueManagerService } from './shared/services/request-queue-manager/request-queue-manager';
import { V3DirectionService } from './shared/services/v3-language/v3-direction.service';

SwiperCore.use([Navigation, Pagination]);

registerLocaleData(localeEnGb);
registerLocaleData(localeAr);
registerLocaleData(localeCy);
registerLocaleData(localeDe);
registerLocaleData(localeFr);

@NgModule({
  imports: [
    NgxsModule.forRoot([], { compatibility: { strictContentSecurityPolicy: true } }), // Do not move to bottom !important
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    TranslocoRootModule,
    MatDialogModule,
  ],
  providers: [
    AccessTokenService,
    MaintenanceService,
    RestClientService,
    PageTitleService,
    PreviousRouteService,
    NavigationHistoryService,
    LikeInfoSubscriptionService,
    DialogService,
    LanguageService,
    TranslocoService,
    RequestQueueManagerService,
    ShareService,
    { provide: Directionality, useExisting: V3DirectionService },
    {
      provide: LEARNER_PLAYLIST_DATA_SERVICE,
      useFactory: learnerPlaylistServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: CORE_TAG_DATA_SERVICE,
      useFactory: tagServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: CORE_COMPETENCE_DATA_SERVICE,
      useFactory: SharedFactoryService.competenceServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    {
      provide: ORGANIZATION_RETRIEVAL_DATA_SERVICE,
      useFactory: organizationRetrievalServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: RESOURCES_FILE_UPLOAD_DATA_SERVICE,
      useFactory: resourcesFileUploadServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: ORGANIZATIONS_FILE_UPLOAD_DATA_SERVICE,
      useFactory: organizationsFileUploadServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: SHARED_CARD_DATA_SERVICE,
      useFactory: sharedResourceServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: SIDEBAR_TREE_DATA_SERVICE,
      useFactory: sharedSidebarTreeServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: SHARED_REVIEW_DATA_SERVICE,
      useFactory: sharedReviewServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: COLLECTOR_DATA_SERVICE,
      useFactory: collectorServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: BOOKING_DATA_SERVICE,
      useFactory: bookingServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: ACCOUNT_DATA_SERVICE,
      useFactory: accountServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: OUTLOOK_DATA_SERVICE,
      useFactory: outlookServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: MY_HUB_DATA_SERVICE,
      useFactory: myHubServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: GOOGLE_CALENDAR_DATA_SERVICE,
      useFactory: googleCalendarServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: PAGES_DATA_SERVICE,
      useFactory: SharedFactoryService.pageServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: PI_DATA_SERVICE,
      useFactory: piServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: LIKE_INFO_DATA_SERVICE,
      useFactory: likeInfoServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: SUBHEADER_DATA_SERVICE,
      useFactory: subheaderServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: LANGUAGES_DATA_SERVICE,
      useFactory: languagesServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: DATA_FORM_DATA_SERVICE,
      useFactory: dataFormServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: INVITATIONS_DATA_SERVICE,
      useFactory: invitationsServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: ADMIN_ENROLMENTS_DATA_SERVICE,
      useFactory: adminEnrolmentsServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: REGISTRATIONS_DATA_SERVICE,
      useFactory: registrationsServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: JOIN_DATA_SERVICE,
      useFactory: joinServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: GROUP_LIST_DATA_SERVICE,
      useFactory: groupListServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: OPTION_LIST_DATA_SERVICE,
      useFactory: optionListServiceFactory,
      deps: [RestClientService],
    },
    {
      provide: WEBSOCKET_SERVICE,
      useFactory: websocketServiceFactory,
      deps: [AccessTokenService],
    },
    {
      provide: TOAST_NOTIFICATION_SERVICE,
      useFactory: toastServiceFactory,
      deps: [NgZone],
    },
    {
      provide: SHARED_LISTS_DATA_SERVICE,
      useFactory: sharedListsServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    {
      provide: PROJECT_DATA_SERVICE,
      useFactory: SharedFactoryService.projectServiceFactory,
      deps: [RestClientService, TranslocoService],
    },
    { provide: EMBEDS_CONTENT_GENERATOR_SERVICE, useFactory: embedsContentGeneratorServiceFactory, deps: [HttpClient] },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslocoService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ],
  bootstrap: [MainComponent],
})
export class AppModule {}

// -------------------------------------------------------------------------
export function learnerPlaylistServiceFactory(client: RestClientService, translocoService: TranslocoService): LearnerPlaylistDataService {
  if (environment.dataProvider === 'mock') {
    return new MockLearnerPlaylistDataService();
  }

  return new ApiLearnerPlaylistDataService(client, translocoService);
}

export function tagServiceFactory(client: RestClientService, translocoService: TranslocoService): TagDataService {
  if (environment.dataProvider === 'mock') {
    return new MockTagDataService();
  }

  return new ApiTagDataService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides the resource file-upload data service. */
export function resourcesFileUploadServiceFactory(client: RestClientService, translocoService: TranslocoService): FileUploadService {
  return fileUploadServiceFactory(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides the organizations file-upload data service. */
export function organizationsFileUploadServiceFactory(client: RestClientService, translocoService: TranslocoService): FileUploadService {
  return fileUploadServiceFactory(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides the organizations file-upload data service. */
export function fileUploadServiceFactory(client: RestClientService, translocoService: TranslocoService): FileUploadService {
  if (environment.dataProvider === 'mock') {
    return new MockFileUploadService();
  }

  return new AwsFileUploadService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides the org details retrieval data service. */
export function organizationRetrievalServiceFactory(
  client: RestClientService,
  translocoService: TranslocoService,
): OrganizationRetrievalDataService {
  if (environment.dataProvider === 'mock') {
    return new MockOrganizationRetrievalDataService();
  }

  return new ApiOrganizationRetrievalDataService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides shared resources data service. */
export function sharedResourceServiceFactory(client: RestClientService, translocoService: TranslocoService): SharedCardDataService {
  if (environment.dataProvider === 'mock') {
    return new MockSharedCardDataService();
  }

  return new ApiSharedCardDataService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides sidebar tree data service. */
export function sharedSidebarTreeServiceFactory(client: RestClientService, translocoService: TranslocoService): SidebarTreeDataService {
  if (environment.dataProvider === 'mock') {
    return new MockSidebarTreeDataService();
  }

  return new ApiSidebarTreeDataService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides shared review data service. */
export function sharedReviewServiceFactory(client: RestClientService): SharedReviewDataService {
  if (environment.dataProvider === 'mock') {
    return new MockSharedReviewDataService();
  }

  return new ApiSharedReviewDataService(client);
}

export function collectorServiceFactory(client: RestClientService): CollectorDataService {
  if (environment.dataProvider === 'mock') {
    return new MockCollectorDataService();
  }

  return new ApiCollectorDataService(client);
}

export function bookingServiceFactory(client: RestClientService, translocoService: TranslocoService): BookingDataService {
  if (environment.dataProvider === 'mock') {
    return new MockBookingDataService();
  }

  return new BookingFormsBookingDataService(new ApiV3BookingDataService(client, translocoService), translocoService);
}

/** Provides the account data service. */
export function accountServiceFactory(client: RestClientService, translocoService: TranslocoService): AccountDataService {
  if (environment.dataProvider === 'mock') {
    return new MockAccountDataService();
  }

  return new ApiAccountDataService(client, translocoService);
}

/** Provides the Microsoft Outlook data service. */
export function outlookServiceFactory(client: RestClientService, translocoService: TranslocoService): OutlookDataService {
  return new ApiOutlookDataService(client, translocoService);
}

/** Provides the Google Calendar data service. */
/** Provides the my hub data service. */
export function myHubServiceFactory(client: RestClientService, translocoService: TranslocoService): MyHubDataService {
  if (environment.dataProvider === 'mock') {
    return new MockMyHubDataService();
  }

  return new ApiMyHubDataService(client, translocoService);
}

/** Provides the analytics data service. */

/** Provides the google calendar data service. */
export function googleCalendarServiceFactory(client: RestClientService, translocoService: TranslocoService): GoogleCalendarDataService {
  return new ApiGoogleCalendarDataService(client, translocoService);
}

export function piServiceFactory(client: RestClientService, translocoService: TranslocoService): PIDataService {
  if (environment.dataProvider === 'mock') {
    return new MockPIDataService();
  }

  return new ApiPIDataService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides like info data service. */
export function likeInfoServiceFactory(client: RestClientService, translocoService: TranslocoService): LikeInfoDataService {
  if (environment.dataProvider === 'mock') {
    return new MockLikeInfoDataService();
  }

  return new ApiLikeInfoDataService(client, translocoService);
}

// -------------------------------------------------------------------------
/** Provides subheader data service. */
export function subheaderServiceFactory(client: RestClientService, translocoService: TranslocoService): SubheaderDataService {
  if (environment.dataProvider === 'mock') {
    return new MockSubheaderDataService();
  }

  return new ApiSubheaderDataService(client, translocoService);
}

export function languagesServiceFactory(client: RestClientService, translocoService: TranslocoService): LanguagesDataService {
  if (environment.dataProvider === 'mock') {
    return new MockLanguagesDataService();
  }

  return new ApiLanguagesDataService(client, translocoService);
}

export function dataFormServiceFactory(client: RestClientService): DataFormDataService {
  if (environment.dataProvider === 'mock') {
    return new MockDataFormDataService();
  }

  return new ApiDataFormDataService(client);
}

export function adminEnrolmentsServiceFactory(client: RestClientService): AdminEnrolmentsDataService {
  if (environment.dataProvider === 'mock') {
    return new MockAdminEnrolmentsDataService();
  }

  return new ApiAdminEnrolmentsDataService(client);
}

export function invitationsServiceFactory(client: RestClientService): InvitationsDataService {
  if (environment.dataProvider === 'mock') {
    return new MockInvitationsDataService();
  }

  return new ApiInvitationsDataService(client);
}

export function optionListServiceFactory(client: RestClientService): OptionListDataService {
  if (environment.dataProvider === 'mock') {
    return new MockOptionListDataService();
  }

  return new ApiOptionListDataService(client);
}

export function groupListServiceFactory(client: RestClientService): GroupListDataService {
  if (environment.dataProvider === 'mock') {
    return new MockGroupListDataService();
  }

  return new ApiGroupListDataService(client);
}

export function websocketServiceFactory(accessTokenService: AccessTokenService): WebsocketService {
  if (environment.dataProvider === 'mock') {
    return new MockWebsocketService();
  }

  return new ApiWebsocketService(accessTokenService);
}

export function toastServiceFactory(ngZone: NgZone): ToastService {
  if (environment.dataProvider === 'mock') {
    return new MockToastService();
  }
  return new ToastServiceImpl(ngZone);
}

export function appInitializerFactory(transloco: TranslocoService) {
  return () => {
    let language = localStorage.getItem(LANGUAGE_KEY);
    if (!language) {
      language = DEFAULT_LANGUAGE_CODE;
    }
    transloco.setDefaultLang(language);
    transloco.setActiveLang(language);
    transloco.setFallbackLangForMissingTranslation({ fallbackLang: FALLBACK_LANGUAGE_CODE });
    return transloco.load(language);
  };
}

export function sharedListsServiceFactory(client: RestClientService, translocoService: TranslocoService): SharedListsDataService {
  if (environment.dataProvider === 'mock') {
    return new MockSharedListsDataService();
  }

  return new ApiSharedListsDataService(client, translocoService);
}

export function embedsContentGeneratorServiceFactory(client: HttpClient): EmbedsContentGeneratorService {
  if (environment.dataProvider === 'mock') {
    return new IframelyEmbedsContentGeneratorService(client);
  }

  return new IframelyEmbedsContentGeneratorService(client);
}
